import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { TimeSeriesData, Interval } from '../hooks/useDashboardMetrics';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export interface TimeSeriesChartProps {
  data: TimeSeriesData | null;
  interval: Interval;
  onIntervalChange: (interval: Interval) => void;
  isLoading?: boolean;
}

const INTERVAL_OPTIONS: Interval[] = ['hour', 'day', 'week'];

const CHART_COLORS = {
  calls: '#3c8dbc',
  callsFill: 'rgba(60, 140, 188, 0.1)',
  tools: '#00a65a',
  toolsFill: 'rgba(0, 166, 90, 0.1)',
};

export function TimeSeriesChart({
  data,
  interval,
  onIntervalChange,
  isLoading = false,
}: TimeSeriesChartProps) {
  const chartData = useMemo(() => {
    if (!data || !data.labels || !data.datasets) {
      return {
        labels: [],
        datasets: [],
      };
    }

    return {
      labels: data.labels,
      datasets: [
        {
          label: 'Processed Calls',
          data: data.datasets[0]?.data ?? [],
          borderColor: CHART_COLORS.calls,
          backgroundColor: CHART_COLORS.callsFill,
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 5,
          yAxisID: 'y',
        },
        {
          label: 'Tools Run',
          data: data.datasets[1]?.data ?? [],
          borderColor: CHART_COLORS.tools,
          backgroundColor: CHART_COLORS.toolsFill,
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 5,
          yAxisID: 'y1',
        },
      ],
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { maxRotation: 45 },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        ticks: { precision: 0 },
        grid: { color: 'rgba(0,0,0,0.05)' },
        title: {
          display: true,
          text: 'Calls',
          color: CHART_COLORS.calls,
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        beginAtZero: true,
        ticks: { precision: 0 },
        grid: { drawOnChartArea: false },
        title: {
          display: true,
          text: 'Tools',
          color: CHART_COLORS.tools,
        },
      },
    },
  };

  if (isLoading) {
    return (
      <div
        style={{
          height: '300px',
          background: '#f0f0f0',
          borderRadius: '4px',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
    );
  }

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        <div className="btn-group btn-group-sm">
          {INTERVAL_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`btn btn-outline-secondary ${interval === opt ? 'active' : ''}`}
              onClick={() => onIntervalChange(opt)}
            >
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div style={{ height: '300px', position: 'relative' }}>
        <Line data={chartData} options={options as any} />
      </div>
    </>
  );
}
